import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { saveAs } from "file-saver";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useFileDownloadMutation } from "../../api/services/client/clientApis";
import { ReactComponent as DownloadIcon } from "../../assets/images/download.svg";
import { getParsedErrorMessage } from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../redux/slices/appState/appStateSlice";
import { MuiBox } from "../box/MuiBox";
import { MuiButtonTertiary } from "../button/MuiButton";
import { MuiTypography } from "../typography/MuiTypography";
import { FileUploadPreviewContainer } from "./FileUploadPreview.style";
import { WarningConfirmationModal } from "../modal/WarningModal";
import { bulkCreateProgramSiteCoreKeys } from "../../pages/bulkCreateProgram/siteCore/siteCoreKeys";
interface FileUploadPreviewProps {
  file: File;
  showCloseIcon?: boolean;
  onFileRemove?: () => void;
  documentName?: string;
  documentId?: string;
  documentType?: string;
  isFromBulkCreate?: boolean;
}

const FileUploadPreview: React.FC<FileUploadPreviewProps> = ({
  file,
  showCloseIcon = true,
  onFileRemove,
  documentName = "",
  documentId,
  documentType = "",
  isFromBulkCreate,
}) => {
  const {
    mutate: downloadFile,
    isError: isErrorOnDownload,
    isPending: isPendingOnDownload,
    isSuccess: isSuccessOnDownload,
    data: dataOnDownload,
    error: errorOnDownload,
  } = useFileDownloadMutation();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isToDownloadFile = useRef<boolean>();

  const { commonLabels, fetchMediaListImage, fetchLabel } = useSiteCore();

  const dispatch = useDispatch();

  const excelImage = fetchMediaListImage(
    bulkCreateProgramSiteCoreKeys.uploadTemplate,
    bulkCreateProgramSiteCoreKeys.excelImage,
    "SmallImage",
  );
  const getUploadLabel = (keyLable: string) => {
    return fetchLabel(bulkCreateProgramSiteCoreKeys.uploadTemplate, keyLable);
  };

  const handleDownloadFile = (isToDownload: boolean) => {
    isToDownloadFile.current = isToDownload;
    if (documentId) {
      downloadFile({ documentId, documentType });
    } else if (isToDownload) {
      saveAs(file, `${file.name}`);
    } else {
      previewDownloadedFile(file);
    }
  };

  const previewDownloadedFile = (file: Blob) => {
    const fileURL = URL.createObjectURL(file);
    // Open file in a new tab
    window.open(fileURL, "_blank");
    // Optionally, revoke the URL later to free memory
    setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
  };

  const handleCloseClick = () => {
    if (isFromBulkCreate) {
      setShowDeleteModal(true);
    } else {
      onFileRemove?.();
    }
  };

  const handleConfirmDelete = () => {
    onFileRemove?.();
    setShowDeleteModal(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPendingOnDownload,
      }),
    );
  }, [isPendingOnDownload]);

  useEffect(() => {
    if (isErrorOnDownload && errorOnDownload) {
      dispatch(
        toggleToast({
          open: true,
          message: getParsedErrorMessage(errorOnDownload),
        }),
      );
    }
  }, [isErrorOnDownload, errorOnDownload]);

  useEffect(() => {
    if (isSuccessOnDownload) {
      if (isToDownloadFile.current) {
        saveAs(dataOnDownload.blob, `${documentName}`);
      } else {
        previewDownloadedFile(dataOnDownload.blob);
      }
    }
  }, [isSuccessOnDownload]);
  return (
    <FileUploadPreviewContainer>
      <MuiBox className="file-info">
        <MuiBox className="file-info-content">
          <MuiBox className="file-preview-wrapper">
            {isFromBulkCreate && (
              <MuiBox className="excel-image">
                <img src={excelImage?.ImageUrl} alt={excelImage?.AltText} />
              </MuiBox>
            )}
            <MuiBox className="file-text-group">
              <MuiButtonTertiary className="file-preview-button">
                <MuiTypography
                  variant="h3SemiBold"
                  className="file-name"
                  onClick={() => handleDownloadFile(false)}
                >
                  {file.name}
                </MuiTypography>
              </MuiButtonTertiary>
              <MuiTypography
                variant="h3SemiBold"
                className={isFromBulkCreate ? "label" : ""}
              >
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </MuiTypography>
            </MuiBox>
          </MuiBox>
        </MuiBox>

        <MuiBox className="file-info-content-icons">
          <DownloadIcon onClick={() => handleDownloadFile(true)} />
          {showCloseIcon && (
            <CloseRoundedIcon
              className="close-image"
              onClick={handleCloseClick}
            />
          )}
        </MuiBox>
      </MuiBox>
      {isFromBulkCreate && showDeleteModal && (
        <WarningConfirmationModal
          modalTitle={commonLabels.confirmation}
          labelContent={getUploadLabel(
            bulkCreateProgramSiteCoreKeys.deleteSmartTemplateConfirmationMessage,
          )}
          show={showDeleteModal}
          onClose={handleCancelDelete}
          onPrimaryButtonClick={handleConfirmDelete}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
    </FileUploadPreviewContainer>
  );
};

export default FileUploadPreview;
